import { splitUrl } from "@repo/fika-shared/src/lib/slug"
import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import { useParams, useSearchParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createEffect, createMemo, createSignal, Show } from "solid-js"
import { urqlClient } from "@/context/graphql-provider"
import { Box } from "@/designSystem/components/box"
import { LinkButton } from "@/designSystem/components/button"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import {
  type PublishedPost,
  PublishedPostFragment,
  PublishedPostView,
} from "@/designSystem/patterns/published-post-view"
import { createFragment, graphql, readFragment } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { createSubscriptionsRouter } from "@/routes/subscriptions-router"
import {
  badgeClass as freeBadgeClass,
  paidBadgeClass,
  upgradeBadgeClass,
} from "./content-access-badge.css"

const SubscriptionItemQuery = graphql(
  `query SubscriptionItem($id: String!, $afterCursor: String!, $beforeCursor: String!, $hasCursor: Boolean!) {
    publishedPost(id: $id) {
      ...PublishedPostView
    }
    older: publishedPosts(subscribed: true, first: 1, after: $afterCursor) @include(if: $hasCursor) {
      edges {
        cursor
        node {
          ...PublishedPostView
        }
      }
    }
    newer: publishedPosts(subscribed: true, last: 1, before: $beforeCursor) @include(if: $hasCursor) {
      edges {
        cursor
        node {
          ...PublishedPostView
        }
      }
    }
  }`,
  [PublishedPostFragment],
)

export function SubscriptionItem() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const getId = createMemo(() => splitUrl(params.postId) || "")
  const getCursor = createMemo(() => {
    const cursor = searchParams.cursor
    if (typeof cursor === "string") return cursor
    return null
  })
  const { paths: subscriptionsPaths } = createSubscriptionsRouter()
  const [showUpgradeModal, setShowUpgradeModal] = createSignal(false)

  createEffect(() => {
    if (params.postId) {
      document.querySelector("[data-main-scroll]")?.scrollTo(0, 0)
    }
  })

  const [result, executeQuery] = createQuery({
    query: SubscriptionItemQuery,
    variables: createMemo(() => ({
      id: getId(),
      afterCursor: getCursor() ?? "",
      beforeCursor: getCursor() ?? "",
      hasCursor: getCursor() !== null,
    })),
    pause: () => !getId(),
  })

  const headerPost = createFragment(
    PublishedPostFragment,
    () => result.data?.publishedPost,
  )

  const previousPost = createMemo(() => {
    const edge = result.data?.newer?.edges?.[0]
    if (!edge) return null
    const node = readFragment(PublishedPostFragment, edge.node)
    return { id: node.id, title: node.title, cursor: edge.cursor }
  })

  const nextPost = createMemo(() => {
    const edge = result.data?.older?.edges?.[0]
    if (!edge) return null
    const node = readFragment(PublishedPostFragment, edge.node)
    return { id: node.id, title: node.title, cursor: edge.cursor }
  })

  createEffect(() => {
    const next = nextPost()
    if (next)
      urqlClient
        .query(SubscriptionItemQuery, {
          id: next.id,
          afterCursor: next.cursor,
          beforeCursor: next.cursor,
          hasCursor: true,
        })
        .toPromise()
  })

  createEffect(() => {
    const prev = previousPost()
    if (prev)
      urqlClient
        .query(SubscriptionItemQuery, {
          id: prev.id,
          afterCursor: prev.cursor,
          beforeCursor: prev.cursor,
          hasCursor: true,
        })
        .toPromise()
  })

  const { LL } = useI18nContext()

  return (
    <MainLayout
      hue="indigo"
      header={
        <Show when={headerPost()}>
          {post => (
            <SubscriptionItemNavigation
              post={post()}
              subscriptionsPaths={subscriptionsPaths}
              onUpgrade={() => setShowUpgradeModal(true)}
            />
          )}
        </Show>
      }
      query={result}
    >
      {data => {
        const post = () =>
          data?.publishedPost
            ? readFragment(PublishedPostFragment, data.publishedPost)
            : null
        return (
          <Show when={post()}>
            {p => (
              <PublishedPostView
                post={p()}
                cursor={getCursor()}
                previousPost={previousPost()}
                nextPost={nextPost()}
                executeQuery={executeQuery}
                showUpgradeModal={showUpgradeModal()}
                setShowUpgradeModal={setShowUpgradeModal}
                previousLabel={LL().subscriptions.previousPost()}
                nextLabel={LL().subscriptions.nextPost()}
                buildPostUrl={post =>
                  subscriptionsPaths().post(
                    { id: post.id, title: post.title },
                    post.cursor,
                  )
                }
              />
            )}
          </Show>
        )
      }}
    </MainLayout>
  )
}

function SubscriptionItemNavigation(props: {
  post: PublishedPost
  subscriptionsPaths: ReturnType<typeof createSubscriptionsRouter>["paths"]
  onUpgrade?: () => void
}) {
  const { LL, locale } = useI18nContext()
  const interfaceLocale = createMemo(() => parseLocale(locale()))
  const buttons = createMemo(() => [
    {
      label: LL().subscriptions.title(),
      href: props.subscriptionsPaths().url,
    } as const,
  ])

  const hasPaidTiers = createMemo(() => {
    const pub = props.post.publication
    return pub.enableMonetization && (pub.subscriptionTiers?.length ?? 0) > 0
  })

  return (
    <NavigationWithButtons
      hue="indigo"
      buttons={buttons()}
      title={props.post.title ?? ""}
    >
      <Box flexDirection="row" gap="8" alignItems="center">
        <Show when={props.post.publication.sub}>
          <Show
            when={props.post.publication.sub?.tier}
            fallback={
              <Show
                when={hasPaidTiers() && props.onUpgrade}
                fallback={
                  <span class={freeBadgeClass}>
                    {LL().subscriptions.subscribed()}
                  </span>
                }
              >
                <button
                  type="button"
                  class={upgradeBadgeClass}
                  onClick={() => props.onUpgrade?.()}
                >
                  {LL().subscriptions.upgradePlan()}
                </button>
              </Show>
            }
          >
            <span class={paidBadgeClass}>
              {translate(
                props.post.publication.sub!.tier!.name,
                interfaceLocale(),
              ) ?? props.post.publication.sub!.tier!.name}
            </span>
          </Show>
        </Show>
        <Show when={props.post.url}>
          {url => (
            <LinkButton hue="indigo" href={url()} target="_blank">
              {LL().subscriptions.viewOriginal()}
            </LinkButton>
          )}
        </Show>
      </Box>
    </NavigationWithButtons>
  )
}
